import { z } from 'zod';

import { TImgUrl } from '@boc/saleor-common/urls';

import { getAttributeValue } from '../products/attributes';
import { PriceFragment } from '../saleor-graphql';
import { generateGoogleProductVariant } from './google';
import { generateKlaviyoProductVariant } from './klaviyo';
import type { TChannel, TProduct, TVariant } from './types';

export const formatPrice = (price: PriceFragment) => {
  return `${price.amount} ${price.currency}`;
};

export const getBrand = (
  product: TProduct,
  defaultBrand: string,
  brandAttributeSlugs?: string[]
) => {
  // Use provided attribute slugs or fall back to default puzzle attributes
  const attributesToCheck = brandAttributeSlugs || [
    'puzzle-brand',
    'puzzle-publisher',
    'puzzle-manufacturer',
  ];

  const brand =
    attributesToCheck
      .map((attr) => getAttributeValue(product.attributes, attr))
      .find((value) => value) || defaultBrand;

  return brand;
};

export interface Row {
  id: string | null;
  title: string | null;
  description?: string | null;
  google_product_category?: string | null;
  product_type?: string | null;
  link: string | null;
  image_link?: string | null;
  condition?: string | null;
  availability?: string | null;
  availability_date?: string | null;
  price: string | null;
  sale_price?: string | null;
  sale_price_effective_date?: string | null;
  brand?: string | null;
  gtin?: string | null;
  mpn?: string | null;
  item_group_id: string | null;
  additional_image_link?: string | null;
  rich_text_description?: string | null;
  custom_label_0?: string | null;
  custom_label_1?: string | null;
  custom_label_2?: string | null;
  custom_label_3?: string | null;
  custom_label_4?: string | null;
}

export const productTypeMapping: Record<string, string> = {
  'board-game': 'Toys & Games > Games > Board Games',
  'jigsaw-puzzle': 'Toys & Games > Puzzles > Jigsaw Puzzles',
  'custom-puzzle': 'Toys & Games > Puzzles > Jigsaw Puzzles',
  'art-craft-set': `Arts & Entertainment > Hobbies & Creative Arts > Arts & Crafts > Art & Craft Kits`,
  'puzzle-sticker-paper': 'Toys & Games > Puzzles > Jigsaw Puzzle Accessories',
};

export const defaultNameGenerator = (product: TProduct, variant: TVariant) => {
  let title: string;

  if (product.variants && product.variants.length > 1) {
    // TODO: Fix this to use attributes to create the variant name
    if (variant.name == variant.sku) title = `${product.name}`;
    else title = `${product.name} - ${variant.name}`;
  } else {
    title = product.name;
  }

  return title;
};

export const schemaFeedType = z.enum(['google', 'facebook', 'klaviyo']);

export type TSchemaFeedType = z.infer<typeof schemaFeedType>;

export type TCustomLabelGenerator = (props: {
  product: TProduct;
  variant: TVariant;
  channel: TChannel;
  locale: string;
}) => string | null | undefined;

export interface TCustomLabels {
  custom_label_2?: TCustomLabelGenerator;
  custom_label_3?: TCustomLabelGenerator;
  custom_label_4?: TCustomLabelGenerator;
}

type TGenerator = (
  feedType: TSchemaFeedType,
  opts: {
    product: TProduct;
    variant: TVariant | null;
    channel: TChannel;
    locale: string;
    defaultBrand: string;
    url: (params: { channel: string; locale: string }, args: { slug: string }) => string;
    absoluteify: (url: string) => string;
    imgUrl?: TImgUrl;
    productImageUrl?: (
      params: { channel: string; locale: string },
      args: { slug: string }
    ) => string;
    brandAttributeSlugs?: string[];
    nameGenerator?: (props: {
      defaultTitle: string;
      product: TProduct;
      variant: TVariant;
      channel: TChannel;
      locale: string;
    }) => string;
    defaultProductType?: string;
    version?: string;
    getProductImage?: (product: TProduct, variant?: TVariant | null) => string | null | undefined;
    customLabels?: TCustomLabels;
  }
) => Row;

export const generateProductVariant = (
  feedType: TSchemaFeedType,
  opts: Parameters<TGenerator>[1]
) => {
  // Note: klaviyo uses a different row format (KlaviyoRow) and should use
  // generateKlaviyoProductVariant directly via the klaviyo-handler
  const generators = {
    google: generateGoogleProductVariant,
    facebook: generateGoogleProductVariant,
    klaviyo: generateGoogleProductVariant, // Fallback for CSV - use klaviyo-handler for JSON
  };

  return generators[feedType](opts);
};

export interface OverrideRow {
  id: string;
  override: string;
  availability: string;
  price: string | null;
  link: string;
  image_link: string;
  sale_price: string | null;
  sale_price_effective_date: string;
}

type TOverrideGenerator = (opts: {
  product: TProduct;
  variant: TVariant;
  channel: TChannel;
  locale: string;
  countryCode: string;
  url: (params: { channel: string; locale: string }, args: { slug: string }) => string;
  absoluteify: (url: string) => string;
  imgUrl?: TImgUrl;
  productImageUrl?: (params: { channel: string; locale: string }, args: { slug: string }) => string;
  version?: string;
  getProductImage?: (product: TProduct, variant?: TVariant | null) => string | null | undefined;
}) => OverrideRow;

export const generateProductVariantOverride: TOverrideGenerator = ({
  product,
  variant,
  channel,
  locale,
  countryCode,
  url,
  absoluteify,
  imgUrl,
  productImageUrl,
  version,
  getProductImage,
}) => {
  const params = { channel: channel.urlSlug, locale };
  let linkUrl = absoluteify(url(params, { slug: product.slug }));

  // Add variant query param for multi-variant products
  const variantCount = product.variants?.length ?? 0;

  if (variantCount > 1) {
    linkUrl = `${linkUrl}?variant=${variant.id}`;
  }

  // Use custom product image URL if provided, otherwise fall back to regular image URL
  let image_link = '';

  if (productImageUrl) {
    image_link = absoluteify(productImageUrl(params, { slug: product.slug }));
  } else {
    // Priority: custom handler -> variant media -> product thumbnail
    const customImage = getProductImage ? getProductImage(product, variant) : null;
    const imageUrl = customImage || variant.media?.[0]?.url || product.thumbnail?.url;

    if (imgUrl && imageUrl) {
      image_link = absoluteify(imgUrl(imageUrl, { width: 1024 }));
    }
  }

  // Append version parameter if provided
  if (image_link && version) {
    const separator = image_link.includes('?') ? '&' : '?';

    image_link = `${image_link}${separator}v=${version}`;
  }

  return {
    id: variant.sku || variant.id,
    override: countryCode,
    availability: product.isAvailable ? 'in stock' : 'out of stock',
    link: linkUrl,
    image_link,
    price: variant.pricing?.priceUndiscounted?.gross
      ? formatPrice(variant.pricing?.priceUndiscounted?.gross)
      : null,
    sale_price: variant.pricing?.price?.gross ? formatPrice(variant.pricing?.price?.gross) : null,

    sale_price_effective_date: '',
  };
};

export type { TProduct, TVariant } from './types';
